import React, { FormEvent, useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import I18n from '@/modules/i18n';
import Text from '@/components/ui/Text';
import SubmitModal from '@/components/ui/Modal/SubmitModal';
import { attachRubric } from '../AddRubricModal/AddRubricModal.utils';
import cn from '@/utils/cn';

interface IUseExistingRubricModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  questionId: number;
}

const UseExistingRubricModal = ({ isOpen, onClose, onSuccess, questionId }: IUseExistingRubricModalProps) => {
  const [rubricIdInput, setRubricIdInput] = useState('');
  const [error, setError] = useState('');

  const handleClose = () => {
    setRubricIdInput('');
    setError('');
    onClose();
  };

  const { isLoading, mutate } = useMutation({
    mutationFn: () => attachRubric(Number(rubricIdInput), questionId),
    mutationKey: ['attachRubric', questionId],
    onError: () => {
      setError(I18n.t('reuse_rubric_id_not_found'));
    },
    onSuccess: () => {
      onSuccess();
      handleClose();
    },
  });

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    setError('');
    if (!rubricIdInput.trim()) {
      setError(I18n.t('reuse_rubric_id_blank'));
      return;
    }
    if (isNaN(Number(rubricIdInput))) {
      setError(I18n.t('reuse_rubric_id_not_found'));
      return;
    }
    mutate();
  };

  return (
    <SubmitModal
      className="tw-w-[30rem]"
      close={handleClose}
      headerText={I18n.t('reuse_existing_rubric')}
      isLoading={isLoading}
      isOpen={isOpen}
      submitText={I18n.t('reuse_rubric')}
      onSubmit={handleSubmit}
    >
      <Text className="tw-font-semibold" size="sm">
        {I18n.t('reuse_rubric_instructions')}
      </Text>
      <ol className="tw-mt-2 tw-mb-6 tw-list-none tw-space-y-1">
        <li>
          <Text size="sm">{I18n.t('reuse_rubric_instructions_step_1')}</Text>
        </li>
        <li>
          <Text size="sm">{I18n.t('reuse_rubric_instructions_step_2')}</Text>
        </li>
        <li>
          <Text size="sm">{I18n.t('reuse_rubric_instructions_step_3')}</Text>
        </li>
      </ol>
      <label className="tw-block" htmlFor="rubric-id-input">
        <Text className="tw-font-semibold" size="sm">{I18n.t('reuse_rubric_id_label')}</Text>
        <input
          className={cn(
            'tw-box-border tw-mt-1 tw-w-full tw-border tw-border-solid tw-border-gray-300 tw-rounded tw-px-3 tw-py-2',
            'tw-text-sm focus:tw-outline-none focus:tw-border-tci-purple',
          )}
          id="rubric-id-input"
          inputMode="numeric"
          type="text"
          value={rubricIdInput}
          onChange={(e) => setRubricIdInput(e.target.value)}
        />
      </label>
      {!!error && (
        <Text className="tw-mt-2 !tw-text-red" size="sm">{error}</Text>
      )}
    </SubmitModal>
  );
};

export default UseExistingRubricModal;
